import _ from 'lodash'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { useRequestHandler } from '@/hooks/useRequestHandler'
import { useSearchParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { AddDoctorFormValues } from '@/components/group/AddDoctorFormDialog'
import AddDoctorDialog from '@/containers/group/AddDoctorDialog'

import SelectStaffAvailabilityStep from '@/pages/create-consultation/components/SelectStaffAvailabilityStep'

import { Slot, WorkingHours } from '@/core/models/schedule.model'
import { useLazyGetScheduleAvailabilitiesQuery } from '@/core/services/schedule'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { addMedecin, selectMedecinCreateRequestTracker } from '@/redux/reducers/medecin.reducer'
import {
  getStaff,
  getStaffAvailability,
  getStaffs,
  getStaffsAvailability,
  getStaffsEvents,
  selectStaffAvailabilities,
  selectStaffCalendsoEventTypes,
  selectStaffGetRequestTracker,
  selectStaffs,
  selectStaffSearchResult,
} from '@/redux/reducers/staff.reducer'

import { sanitizeStaffCreation } from '@/utils/staff'
import { isStaff } from '@/utils/type-guards'

import { CreateConsultationStepperContext } from '../CreateConsultationStepper'

dayjs.extend(isoWeek)

const limitOptions = [5, 10, 15]

export type SelectStaffAvailabilityStepContainerProps = {}

export function SelectStaffAvailabilityStepContainer(props: SelectStaffAvailabilityStepContainerProps) {
  const { config, setValue, initialValues, values, previousStep } = useContext(CreateConsultationStepperContext)
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [getStaffAvailabilities] = useLazyGetScheduleAvailabilitiesQuery()

  const [addDoctorDialogOpen, setAddDoctorDialogOpen] = useState<boolean>(false)
  const [preselectedDoctorId, setPreselectedDoctorId] = useState<string>()
  const [staffAvailabilities, setStaffAvailabilities] = useState<Record<string, { busySlots: Slot[]; workingHours: WorkingHours[]; timeZone?: string }>>({})

  const authUser = useAppSelector(selectAuthUser)
  const staffs = useAppSelector(selectStaffs)
  const staffCalendsoEventTypes = useAppSelector(selectStaffCalendsoEventTypes)
  const staffCalendsoAvailabilities = useAppSelector(selectStaffAvailabilities)
  const staffSearchResult = useAppSelector(selectStaffSearchResult)

  const availabilitiesByStaff = useMemo(() => {
    const availabilities = _.cloneDeep(staffAvailabilities)
    if (initialValues?.medecinScheduleConsultationReason) {
      for (const staffId in availabilities) {
        const workingHours = availabilities[staffId].workingHours
        availabilities[staffId] = {
          ...availabilities[staffId],
          workingHours: workingHours.filter((workingHour) => workingHour.kind === initialValues.medecinScheduleConsultationReason),
        }
      }
    }
    return availabilities
  }, [staffAvailabilities, initialValues])

  const filteredStaffs = useMemo(
    () =>
      !authUser || !isStaff(authUser)
        ? []
        : _.map(
            _.filter(staffs, preselectedDoctorId ? ['_id', preselectedDoctorId] : ['status', authUser.status === 'MEDECIN' ? 'PDS' : 'MEDECIN']),
            (staff) => ({
              id: staff._id,
              email: staff.email,
              name: staff.userInfo.name,
              speciality: staff.speciality,
              status: staff.status as string,
              surname: staff.userInfo.surname,
            })
          ),
    [authUser, staffs, preselectedDoctorId]
  )

  const calendsoAppointmentTypesByStaff = useMemo(() => {
    return _.reduce(
      staffCalendsoEventTypes,
      (result: Record<string, Array<{ id: number; type: string; duration: number }>>, staffCalendsoEventType) => {
        if (!result[staffCalendsoEventType.id]) {
          result[staffCalendsoEventType.id] = _.map(staffCalendsoEventType.events, (eventType) => ({
            id: eventType.id,
            type: eventType.eventTypeName,
            duration: eventType.duree,
          }))
        }
        return result
      },
      {}
    )
  }, [staffCalendsoEventTypes])

  const calendsoAvailabilitiesByStaff = useMemo(() => {
    return _.reduce(
      staffCalendsoAvailabilities,
      (result: Record<string, { busySlots: Slot[]; workingHours: WorkingHours[]; timeZone?: string }>, staffAvailability) => {
        if (!result[staffAvailability.id]) {
          result[staffAvailability.id] = {
            busySlots: staffAvailability.availability.busy,
            workingHours: staffAvailability.availability.workingHours,
            timeZone: staffAvailability.availability.timeZone,
          }
        }
        return result
      },
      {}
    )
  }, [staffCalendsoAvailabilities])

  const staffGetRequestHandler = useRequestHandler<string>({
    onError: () => {
      searchParams.delete('doctor')
      setSearchParams(searchParams.toString())
    },
    onSuccess: (doctorId) => {
      if (doctorId) setPreselectedDoctorId(doctorId)
    },
    requestTrackerSelector: selectStaffGetRequestTracker,
  })

  const addDoctorRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.medecin.create.success'), { variant: 'success' })
      setAddDoctorDialogOpen(false)
    },
    onError: () => enqueueSnackbar(t('notification.medecin.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectMedecinCreateRequestTracker,
  })

  useEffect(() => {
    if (previousStep === 0) return
    if (values.medecin) setValue('medecin', undefined)
    if (values.pds) setValue('pds', undefined)
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const doctorId = searchParams.get('doctor') ?? initialValues.medecin
    if (doctorId) staffGetRequestHandler.dispatch(getStaff(doctorId))
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (searchParams.has('doctor') || initialValues.medecin) return
    const query = {
      name: '',
      surname: '',
    }
    const options = {
      limit: 5,
      skip: 0,
      orderBy: '',
    }
    dispatch(getStaffs(query, options))
  }, [dispatch, initialValues.medecin, searchParams])

  useEffect(() => {
    if (!filteredStaffs.length) return
    dispatch(
      getStaffsAvailability({
        filters: filteredStaffs.map((staff) => {
          return {
            staffId: staff.id,
            dateFrom: dayjs().toISOString(),
            dateTo: dayjs().add(6, 'day').toISOString(),
          }
        }),
      })
    )
    dispatch(
      getStaffsEvents(
        filteredStaffs.map((staff) => {
          return {
            staffId: staff.id,
          }
        })
      )
    )
  }, [dispatch, filteredStaffs])

  useEffect(() => {
    if (!filteredStaffs.length) return

    getStaffAvailabilities({
      dateFrom: dayjs().isoWeekday(1).startOf('day').toISOString(),
      dateTo: dayjs().isoWeekday(1).add(6, 'day').endOf('day').toISOString(),
      owners: filteredStaffs.map((staff) => staff.id),
    })
      .unwrap()
      .then(setStaffAvailabilities)
  }, [getStaffAvailabilities, filteredStaffs])

  const handleSearchStaffs = useDebouncedCallback((text: string) => {
    if (searchParams.has('doctor') && text === '') {
      const doctorId = searchParams.get('doctor')
      if (doctorId) staffGetRequestHandler.dispatch(getStaff(doctorId))
      return
    }
    if (searchParams.has('doctor') && text !== '' && !!preselectedDoctorId) {
      setPreselectedDoctorId(undefined)
    }
    const [name] = text.split(' ')
    const query = {
      name: name,
      surname: name,
    }
    const options = {
      limit: 10,
      skip: 0,
      orderBy: '',
    }
    dispatch(getStaffs(query, options))
  }, 300)

  const handleSearchStaffAvailability = useCallback(
    async (dateFrom: Date, dateTo: Date, staff: typeof filteredStaffs[0]) => {
      dispatch(
        getStaffAvailability(staff.surname.concat(staff.name).toLowerCase().replaceAll(' ', '-'), dateFrom.toISOString(), dateTo.toISOString(), staff.id)
      )
      const staffAvailability = await getStaffAvailabilities({
        dateFrom: dayjs(dateFrom).startOf('day').toISOString(),
        dateTo: dayjs(dateTo).endOf('day').toISOString(),
        owners: [staff.id],
      }).unwrap()
      setStaffAvailabilities((prevStaffAvailabilities) => _.merge({}, prevStaffAvailabilities, staffAvailability))
    },
    [dispatch]
  )

  const handleSelectStaff = useCallback(
    (staffId: string) => {
      if (isStaff(authUser) && authUser?.status === 'PDS') {
        setValue('medecin', staffId)
      } else if (isStaff(authUser) && authUser?.status === 'MEDECIN') {
        setValue('pds', staffId)
      }
      // setValue('event', event)
    },
    [authUser, setValue]
  )

  const handleSelectStartDate = useCallback(
    (startDate: Date | null, duration?: number, appointmentTypeId?: number) => {
      setValue('startDate', startDate ? startDate.toISOString() : undefined)
      setValue('expectedDuration', duration)
      setValue('eventType', appointmentTypeId !== undefined ? `${appointmentTypeId}` : undefined)
    },
    [setValue]
  )

  const handlePageChange = (page: number, rowsPerPage: number) => {
    dispatch(getStaffs({ name: '', surname: '' }, { limit: rowsPerPage, skip: (page - 1) * rowsPerPage }))
  }

  const toggleAddDoctorDialog = () => {
    setAddDoctorDialogOpen(!addDoctorDialogOpen)
  }

  const handleAddDoctor = (values: AddDoctorFormValues) => {
    addDoctorRequestHandler.dispatch(addMedecin(sanitizeStaffCreation(values)))
  }

  return (
    <>
      <SelectStaffAvailabilityStep
        calendsoAppointmentTypesByStaff={calendsoAppointmentTypesByStaff}
        availabilitiesByStaff={availabilitiesByStaff}
        calendsoAvailabilitiesByStaff={calendsoAvailabilitiesByStaff}
        defaultStartDate={initialValues.startDate ? dayjs(initialValues.startDate).toDate() : undefined}
        disableAddStaff={!!initialValues.medecin}
        disableSearchStaffs={!!initialValues.medecin}
        selectedStaff={initialValues.medecin}
        staffs={filteredStaffs}
        isDoctor={isStaff(authUser) && authUser?.status === 'MEDECIN'}
        onOpenAddDoctorDialog={toggleAddDoctorDialog}
        onSearchStaffAvailability={handleSearchStaffAvailability}
        onSelectStaff={handleSelectStaff}
        onSelectStartDate={handleSelectStartDate}
        onTextSearch={handleSearchStaffs}
        onPageChange={handlePageChange}
        rowsOptions={limitOptions}
        totalStaffs={_.get(staffSearchResult, 'totalCount', 0)}
        showOnlyAvailability={config?.showOnlyAvailability}
      />
      {isStaff(authUser) && authUser?.status === 'PDS' && (
        <AddDoctorDialog
          isAdding={addDoctorRequestHandler.requestTracker.loading}
          open={addDoctorDialogOpen}
          onAddDoctor={handleAddDoctor}
          onClose={toggleAddDoctorDialog}
        />
      )}
    </>
  )
}

export default SelectStaffAvailabilityStepContainer
